import { useNavigate } from '@tanstack/react-router'
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  XIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Link } from '@/components'
import { Button } from '@/components/ui'
import { useToast } from '@/hooks'
import {
  compareDuplicateMembers,
  type DuplicateGroup,
  duplicateReviewPath,
  duplicatesPath,
  sharedMemberPage,
  widenPageRange,
} from '@/modules/insights/utils'
import {
  DeleteTransactionsDialog,
  type Transaction,
  useInsightsMutations,
  useStatements,
} from '@/modules/projects'
import { useDialogActions } from '@/stores'
import { formatCalendarDate, numberToCurrency } from '@/utils'
import { MembersPane } from './MembersPane'
import {
  StatementPane,
  type StatementPaneHandle,
  type StatementPaneSource,
} from './StatementPane'

interface DuplicateReviewProps {
  slug: string
  group: DuplicateGroup
  worklist: DuplicateGroup[]
}

export const DuplicateReview = ({
  slug,
  group,
  worklist,
}: DuplicateReviewProps) => {
  const navigate = useNavigate()
  const statements = useStatements()
  const { successToast, errorToast } = useToast()
  const { deleteDuplicates } = useInsightsMutations()
  const { openDialog } = useDialogActions()

  const members = useMemo(
    () => [...group.transactions].sort(compareDuplicateMembers),
    [group.transactions],
  )
  const [activeMemberId, setActiveMemberId] = useState(members[0]?.id ?? 0)
  const active = members.find(({ id }) => id === activeMemberId) ?? members[0]
  const statementPane = useRef<StatementPaneHandle>(null)

  const showMember = (member: Transaction) => {
    setActiveMemberId(member.id)
    statementPane.current?.showPage(member.statementId, member.sourcePage)
  }

  // The statements store is refreshed by websocket, so it holds a fresher file URL than the copy
  // stamped on the transaction. Fall back to the transaction for a row whose statement is gone.
  const sources = useMemo(() => {
    const byStatementId = new Map<number, StatementPaneSource>()
    for (const member of members) {
      const statement = statements.find(({ id }) => id === member.statementId)
      const existing = byStatementId.get(member.statementId)
      byStatementId.set(member.statementId, {
        statementId: member.statementId,
        fileName: statement?.fileName ?? 'Unknown file',
        fileUrl: statement?.fileUrl ?? member.statementFileUrl ?? '',
        pageRange: widenPageRange(
          existing?.pageRange ?? null,
          member.sourcePage,
        ),
        firstMemberPage: existing?.firstMemberPage ?? member.sourcePage,
      })
    }
    return [...byStatementId.values()]
  }, [members, statements])

  const sharedPage = sharedMemberPage(members)

  const fileNameFor = (statementId: number) =>
    sources.find((source) => source.statementId === statementId)?.fileName ??
    'Unknown file'

  const close = () => navigate({ to: duplicatesPath(slug) })

  // A full-screen surface is expected to take Escape, which a route does not give us for free. The
  // delete confirmation is a Radix dialog with its own Escape, so leave the key alone while it is up.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (document.querySelector('[role="dialog"]')) return
      navigate({ to: duplicatesPath(slug) })
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate, slug])

  const position = worklist.findIndex(({ id }) => id === group.id)
  const goToGroup = (index: number) => {
    const next = worklist[index]
    if (next) navigate({ to: duplicateReviewPath(slug, next.id) })
  }

  const handleDismiss = () => {
    deleteDuplicates(
      slug,
      group.transactions.map(({ id }) => id),
    )
      .then(() => successToast('Marked as not duplicates'))
      .catch((error) =>
        errorToast('Could not mark this group as reviewed', error),
      )
  }

  const requestDelete = (member: Transaction) =>
    openDialog(DeleteTransactionsDialog.id, {
      transactions: [member],
      variant: 'transactions',
    })

  return (
    // Full screen over the project shell, the same as the reconciliation workspace: comparing a row
    // against its page needs the height, and the tabs underneath are not part of the job.
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex items-start justify-between gap-3 border-b p-4">
        <div className="min-w-0 space-y-1.5">
          <Button
            theme="ghost"
            size="sm"
            asChild
            className="-ml-2 h-7 gap-1.5 text-xs text-muted-foreground"
          >
            <Link to={duplicatesPath(slug)} preload="intent">
              <ChevronLeftIcon className="size-4" />
              Duplicates
            </Link>
          </Button>
          <h1 className="truncate text-base font-semibold text-foreground">
            {group.description}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{formatCalendarDate(group.date)}</span>
            <span>·</span>
            <span className="tabular-nums">
              {numberToCurrency(group.amount)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <CopyIcon className="size-3" />
              {members.length} matches
            </span>
            {sources.length > 1 && (
              <span>across {sources.length} statements</span>
            )}
            {sharedPage != null && sources.length === 1 && (
              <span>every copy prints on page {sharedPage}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {worklist.length > 1 && position >= 0 && (
            <div className="flex items-center gap-1">
              <Button
                theme="outline"
                size="sm"
                isIcon
                className="cursor-pointer"
                disabled={position === 0}
                onClick={() => goToGroup(position - 1)}
              >
                <ChevronLeftIcon className="size-4" />
                <span className="sr-only">Previous group</span>
              </Button>
              <span className="w-16 text-center text-xs tabular-nums text-muted-foreground">
                {position + 1} of {worklist.length}
              </span>
              <Button
                theme="outline"
                size="sm"
                isIcon
                className="cursor-pointer"
                disabled={position === worklist.length - 1}
                onClick={() => goToGroup(position + 1)}
              >
                <ChevronRightIcon className="size-4" />
                <span className="sr-only">Next group</span>
              </Button>
            </div>
          )}
          <Button
            theme="outline"
            size="sm"
            className="cursor-pointer gap-1.5"
            onClick={handleDismiss}
          >
            <CheckIcon className="size-3.5" />
            Not duplicates
          </Button>
          <Button
            theme="ghost"
            size="sm"
            isIcon
            className="cursor-pointer"
            onClick={close}
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex w-1/2 flex-col border-r">
          <StatementPane
            ref={statementPane}
            sources={sources}
            activeStatementId={active?.statementId ?? 0}
            activePage={active?.sourcePage ?? null}
          />
        </div>
        <div className="w-1/2 overflow-auto p-4 scrollbar-thin scrollbar-thumb-neutral-300">
          <MembersPane
            members={members}
            fileNameFor={fileNameFor}
            activeMemberId={active?.id ?? 0}
            onSelect={showMember}
            onDelete={requestDelete}
          />
        </div>
      </div>
    </div>
  )
}
